import { useEffect, useRef } from 'react';
import React from 'react';
import Konva from 'konva';
import { createRenderer } from '../../renderers';
import { syncConnectorsWithElements } from '../../utils/connectorSync';
import { ConnectorElement } from '../../editing/types';
import { UseCanvasStateReturn } from './useCanvasState';

export function useElementRendering(
  state: UseCanvasStateReturn,
  width: number,
  height: number
) {
  const {
    containerRef,
    canvasRef,
    selectionManagerRef,
    dragManagerRef,
    connectorManagerRef,
    historyManagerRef,
    anchorManagerRef,
    elementsRef,
    modeRef,
    connectorModeRef,
    renderedIdsRef,
    elements,
    setElements,
    selectedIds,
    setIsTransforming,
  } = state;

  // Render elements when they change
  useEffect(() => {

    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Get current and new element IDs
    const currentIds = new Set(canvas.getElementIds());
    const newIds = new Set(elements.map(el => el.id));

    // Remove elements that are no longer in the list
    currentIds.forEach(id => {
      if (!newIds.has(id)) {
        canvas.removeElement(id);
        if (renderedIdsRef.current) {
          renderedIdsRef.current.delete(id);
        }
      }
    });
    
    // Update existing elements or render new ones
    elements.forEach((element) => {
      // If element already exists, update its renderer
      if (currentIds.has(element.id)) {
        const renderer = canvas.getRenderer(element.id);
        if (renderer) {
          // Update the renderer with any changes to the element
          // Pass the full element as updates - renderer.update handles Partial<EditableElement>
          renderer.update({
            x: element.x,
            y: element.y,
            width: element.width,
            height: element.height,
            rotation: element.rotation,
            data: element.data,
          });
        }
        return;
      }
      try {
  
        const renderer = createRenderer(element);
        const node = canvas.addElement(element, renderer);
 
        
        // Attach click handler for selection
        node.on('click', (e: Konva.KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true; // Prevent stage click from triggering
          
          // Only handle selection in edit mode (use ref for current value)
          if (modeRef.current !== 'edit') return;
          
          // Handle connector mode: start connector creation from clicked shape
          if (connectorModeRef.current && canvasRef.current) {
            const stage = node.getStage();
            if (stage) {
              const pointerPos = stage.getPointerPosition();
              if (pointerPos) {
                const pos = canvasRef.current.screenToLayer(pointerPos);
                const isPrecise = e.evt.ctrlKey || e.evt.metaKey;
                connectorManagerRef.current?.startFromShape(element.id, pos, isPrecise);
              }
            }
            return;
          }
          
          // Check if element is already selected (use selection manager for current state)
          const currentSelectedIds = selectionManagerRef.current?.getSelectedIds() || [];
          const isAlreadySelected = currentSelectedIds.includes(element.id);
          
          // If element is already selected and it's a text-editable element, open text editor
          // Only if not using multi-select modifiers (shift/ctrl/cmd)
          if (isAlreadySelected && !e.evt.shiftKey && !e.evt.ctrlKey && !e.evt.metaKey) {
            const textEditableTypes = ['sticky', 'text', 'shape', 'organizer', 'section'];
            if (textEditableTypes.includes(element.type)) {
              // Dispatch double-click event to open text editor
              const customEvent = new CustomEvent('elementdblclick', { detail: element });
              containerRef.current?.dispatchEvent(customEvent);
              return;
            }
          }
          
          const isMultiSelect = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
          selectionManagerRef.current?.selectElement(element.id, isMultiSelect);
        });
        
        // Attach double-click handler for text editing
        node.on('dblclick', (e: Konva.KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true;
          if (modeRef.current !== 'edit') return;
          
          const customEvent = new CustomEvent('elementdblclick', { detail: element });
          containerRef.current?.dispatchEvent(customEvent);
        });
        
        // Make node draggable in edit mode
        node.draggable(modeRef.current === 'edit');
        
        // Store position before drag for undo
        let dragStartPosition = { x: element.x, y: element.y };
        
        // Attach drag handlers
        node.on('dragstart', () => {
          if (modeRef.current !== 'edit') return;
          // Store the position at drag start for undo
          dragStartPosition = { x: node.x(), y: node.y() };
          dragManagerRef.current?.startDrag([element.id]);
          setIsTransforming(true);
          // Hide anchors during drag
          anchorManagerRef.current?.setTransforming(true);
        });
        
        node.on('dragmove', () => {
          if (modeRef.current !== 'edit') return;
          const pos = node.position();
          
          // Ensure anchors stay hidden during drag (in case setElements is called)
          anchorManagerRef.current?.setTransforming(true);
          
          // Get current element with updated position for snapping
          const currentElement = { ...element, x: pos.x, y: pos.y };
          const snapResult = dragManagerRef.current?.onDragMove(currentElement);
          
          // Apply snapping if available
          let finalPos = pos;
          if (snapResult) {
            if (snapResult.x !== undefined) {
              node.x(snapResult.x);
              finalPos = { ...finalPos, x: snapResult.x };
            }
            if (snapResult.y !== undefined) {
              node.y(snapResult.y);
              finalPos = { ...finalPos, y: snapResult.y };
            }
          }
          
          // Update elements ref with current position for connector syncing
          // Get latest element from ref to ensure we have latest data
          if (elementsRef.current) {
            const latestElement = elementsRef.current.find((el) => el.id === element.id) || element;
            const updatedElement = { ...latestElement, x: finalPos.x, y: finalPos.y };
            elementsRef.current = elementsRef.current.map((el) => 
              el.id === element.id ? updatedElement : el
            );
          }
          
          // Sync connectors in real-time during drag
          if (elementsRef.current) {
            const connectors = elementsRef.current.filter((el) => el.type === 'connector') as ConnectorElement[];
            if (connectors.length > 0 && canvasRef.current) {
              const syncedConnectors = syncConnectorsWithElements(connectors, elementsRef.current, element.id);
              // Update connector renderers with synced data and position in real-time
              syncedConnectors.forEach((connector) => {
                const connectorRenderer = canvasRef.current?.getRenderer(connector.id);
                if (connectorRenderer) {
                  // Update both position and data to ensure connector moves with the shape
                  connectorRenderer.update({ 
                    x: connector.x, 
                    y: connector.y, 
                    width: connector.width, 
                    height: connector.height,
                    data: connector.data 
                  });
                }
                // Also update the element in elementsRef to keep it in sync
                if (elementsRef.current) {
                  const index = elementsRef.current.findIndex(el => el.id === connector.id);
                  if (index !== -1) {
                    elementsRef.current[index] = connector;
                  }
                }
              });
              // Force redraw to show connector updates during drag
              canvasRef.current.batchDraw();
            }
          }
          
          // Update rotation triggers position during drag
          selectionManagerRef.current?.updateRotationTriggers();
        });
        
        node.on('dragend', () => {
          if (modeRef.current !== 'edit') return;
          const pos = node.position();
          dragManagerRef.current?.endDrag();
          
          // Update the renderer's internal element data
          const renderer = canvasRef.current?.getRenderer(element.id);
          if (renderer) {
            renderer.update({ x: pos.x, y: pos.y });
          }
          
          // Update elements ref for managers without triggering re-render
          if (elementsRef.current) {
            elementsRef.current = elementsRef.current.map((el) => 
              el.id === element.id ? { ...el, x: pos.x, y: pos.y } : el
            );
            
            // Update all managers with current elements
            dragManagerRef.current?.setElements(elementsRef.current);
            connectorManagerRef.current?.setElements(elementsRef.current);
            historyManagerRef.current?.updateElementsRef(elementsRef.current);
            anchorManagerRef.current?.setElements(elementsRef.current);
            
            // Sync connectors when element is moved
            const connectors = elementsRef.current.filter((el) => el.type === 'connector') as ConnectorElement[];
            if (connectors.length > 0 && canvasRef.current) {
              const syncedConnectors = syncConnectorsWithElements(connectors, elementsRef.current, element.id);
              // Update connector renderers with synced data and position
              syncedConnectors.forEach((connector) => {
                const connectorRenderer = canvasRef.current?.getRenderer(connector.id);
                if (connectorRenderer) {
                  // Update both position and data to ensure connector is positioned correctly
                  connectorRenderer.update({ 
                    x: connector.x, 
                    y: connector.y, 
                    width: connector.width, 
                    height: connector.height,
                    data: connector.data 
                  });
                }
                // Also update the element in elementsRef to keep it in sync
                if (elementsRef.current) {
                  const index = elementsRef.current.findIndex(el => el.id === connector.id);
                  if (index !== -1) {
                    elementsRef.current[index] = connector;
                  }
                }
              });
            }
          }
          
          // Update rotation triggers position after drag ends
          selectionManagerRef.current?.updateRotationTriggers();
          
          // Record position change for undo (without triggering re-render)
          historyManagerRef.current?.recordPositionChange(
            element.id,
            dragStartPosition,
            { x: pos.x, y: pos.y }
          );
          
          setIsTransforming(false);
          // Re-enable anchors after drag ends
          anchorManagerRef.current?.setTransforming(false);
        });
        
        // Track that this element has been rendered
        if (renderedIdsRef.current) {
          renderedIdsRef.current.add(element.id);
        }
      } catch (error) {
        console.error(`Failed to render element ${element.id}:`, error);
      }
    });
    
    // Update selection
    selectionManagerRef.current?.refresh();
    
    // Update managers with current elements
    dragManagerRef.current?.setElements(elements);
    connectorManagerRef.current?.setElements(elements);
    historyManagerRef.current?.updateElementsRef(elements);
    anchorManagerRef.current?.setElements(elements);
    
    // Force redraw after all updates
    canvas.batchDraw();
    
    // Cleanup function for React StrictMode
    return () => {
      canvas.clearElements();
      if (renderedIdsRef.current) {
        renderedIdsRef.current.clear();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  // Setup history manager with setElements
  useEffect(() => {
    historyManagerRef.current?.setElementsRef(elements, setElements);
  }, [elements, setElements]);

  // Listen to selection changes from SelectionManager and sync to React state
  const isSyncingFromManager = useRef(false);
  const lastManagerSelection = useRef<string[]>([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const selectionManager = selectionManagerRef.current;
    if (!canvas || !selectionManager || !state.selectElements) return;
    
    const unsubscribe = canvas.on('selectionChange', (payload: { selectedIds: string[] }) => {
      // Store what SelectionManager selected FIRST
      lastManagerSelection.current = [...payload.selectedIds]; // Copy array
      
      // Update anchor manager immediately when selection changes from SelectionManager
      anchorManagerRef.current?.setSelectedIds(payload.selectedIds);
      
      // Get current selectedIds from state (use a ref to avoid stale closure)
      const currentSelectedIds = selectedIds;
      
      // Only update React state if it differs (avoid infinite loops)
      // Use JSON.stringify for deep comparison
      const currentIdsStr = JSON.stringify([...currentSelectedIds].sort());
      const newIdsStr = JSON.stringify([...payload.selectedIds].sort());
      if (currentIdsStr !== newIdsStr) {
        isSyncingFromManager.current = true; // Flag to prevent reverse sync
        state.selectElements?.(payload.selectedIds, true);
      }
    });
    
    return unsubscribe;
  }, [canvasRef, selectionManagerRef, state.selectElements, selectedIds]);

  // Sync selection from React context to SelectionManager
  // Pass emit=false to avoid circular updates (React -> Konva -> emit -> React -> ...)
  // Skip sync if this was triggered by SelectionManager's selectionChange event
  useEffect(() => {
    if (isSyncingFromManager.current) {
      isSyncingFromManager.current = false;
      // Update lastManagerSelection to match React state (they should be in sync now)
      lastManagerSelection.current = [...selectedIds];
      return; // Skip sync if we just updated from SelectionManager
    }
    
    // Check if React state matches what SelectionManager last selected
    const reactIdsStr = JSON.stringify([...selectedIds].sort());
    const managerIdsStr = JSON.stringify([...lastManagerSelection.current].sort());
    if (reactIdsStr === managerIdsStr && reactIdsStr !== '[]') {
      // Already in sync, no need to update (but allow sync if both are empty - that's a clear operation)
      return;
    }
    
    selectionManagerRef.current?.selectElements(selectedIds, true, false);
    // Update anchor manager - anchors should hide when element is selected
    anchorManagerRef.current?.setSelectedIds(selectedIds);
    // Update lastManagerSelection to match React state
    lastManagerSelection.current = [...selectedIds];
  }, [selectedIds]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && canvasRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        canvasRef.current.resize(rect.width, rect.height);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle width/height prop changes
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.resize(width, height);
    }
  }, [width, height]);
}
